import { css } from '@emotion/react';
import React, { useId, useState } from 'react';

import { Flex } from '@/box';
import InfoButton from '@/hox-core/components/controls/InfoButton.component';
import { Spacer } from '@/ui/spacing';
import { useTheme } from '@/ui/theme';
import { BodySmall, Caption, Text } from '@/ui/typography';

interface DataTableRowProps {
  label: string | React.ReactNode;
  value: string | React.ReactNode;
  helpText?: React.ReactNode;
  hideColon?: boolean;
  testId?: string;
  valueFontWeight?: React.CSSProperties['fontWeight'];
  discount?: string | React.ReactNode;
  title?: string | React.ReactNode;
  description?: string | React.ReactNode;
  opacity?: number;
}

/**
 * Used to render a key-value pair, usually as a table row.
 */
export const DataTableRow = (props: DataTableRowProps) => {
  const [isHelpTextVisible, setIsHelpTextVisible] = useState(false);
  const {
    colors,
    componentProperties: { staySummary },
  } = useTheme();
  const id = useId();
  const testId = `data-row--${props.testId}`;

  return (
    <Flex flexDirection="column">
      {props.title && (
        <Text style="heading6" css={{ margin: '12px 0' }}>
          {props.title}
        </Text>
      )}
      <div
        data-test={testId}
        className="field-section"
        role="row"
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          line-height: 24px;
          padding: 2px 0;

          .label {
            align-self: flex-start;

            ${staySummary?.color && `color: ${staySummary.color};`}
            span {
              ${staySummary?.color && `color: ${staySummary.color};`}
            }

            strong {
              font-weight: 400;
            }
          }
        `}
      >
        <div className="label" id={id} data-test="data-row-label">
          <BodySmall
            inline
            css={{
              opacity: props.opacity ?? 0.56,
              ...staySummary?.dataRow?.label,
            }}
          >
            {props.label}
            {props.hideColon ? '' : ':'}
          </BodySmall>
          {props.helpText && (
            <span style={{ marginInlineStart: '8px' }} data-test="help-text">
              <InfoButton
                onClick={() => setIsHelpTextVisible((state) => !state)}
              />
            </span>
          )}
        </div>
        <div
          aria-labelledby={id}
          role="gridcell"
          data-test="data-row-value"
          css={{ display: 'flex', gap: '8px' }}
        >
          {props.discount && (
            <BodySmall
              css={{
                textAlign: 'right',
                ...staySummary?.dataRow?.value,
                color: colors.sale,
                textDecoration: 'line-through',
                fontWeight:
                  props.valueFontWeight ??
                  staySummary?.dataRow?.value?.fontWeight,
              }}
            >
              {props.discount}
            </BodySmall>
          )}
          <BodySmall
            css={{
              textAlign: 'right',
              ...staySummary?.dataRow?.value,
              fontWeight:
                props.valueFontWeight ??
                staySummary?.dataRow?.value?.fontWeight,
            }}
          >
            {props.value}
          </BodySmall>
        </div>
      </div>
      {props.helpText && isHelpTextVisible && (
        <>
          <Spacer s="xxs" />
          <BodySmall>{props.helpText}</BodySmall>
          <Spacer s="xxs" />
        </>
      )}
      {props.description && (
        <Caption
          css={{
            opacity: 0.56,
            margin: '12px 0',
            ...staySummary?.dataRow?.label,
          }}
        >
          {props.description}
        </Caption>
      )}
    </Flex>
  );
};

export default DataTableRow;
